/**
 * RD-SEC-015 (2026-09-27 "password modal grows after opening" fix) —
 * module-level, in-memory ONLY (never localStorage) snapshot of a user's
 * newest verified TOTP factor, so `PasswordChangeModal` can pick its path
 * ('2fa' in-app step-up vs 'email' link) on its FIRST render instead of
 * racing `listFactors()` against the open animation. `TwoFactorSettingsRow`
 * already fetches this on mount for its own row — this module lets
 * `PasswordChangeModal` read that answer instead of starting blind. Mirrors
 * `mfaResetNoteCache.js`.
 *
 * UI hint only — the server (GoTrue AAL2 + `challengeAndVerify`) remains the
 * real security boundary; a stale/wrong snapshot only changes which SCREEN
 * paints first, never what the server accepts. Per-user + cleared at
 * sign-out (`clearPerUserDeviceState`) so a shared browser never reads a
 * prior user's answer.
 */

/** userId -> { verified: object|null } */
const cache = new Map();

/** Store the answer from a SUCCESSFUL real `listFactors()` read. No-op
 *  without a userId. */
export function rememberTotpFactors(userId, verifiedFactor) {
  if (!userId) return;
  cache.set(userId, { verified: verifiedFactor || null });
}

/** Synchronous read — `undefined` when unknown (never remembered, or
 *  forgotten), so a caller can tell "no 2FA" (`verified: null`) apart from
 *  "haven't checked yet". */
export function peekTotpFactors(userId) {
  if (!userId) return undefined;
  return cache.get(userId);
}

/** Drop `userId`'s snapshot — call right after a write that can change the
 *  factor list, before the refresh that always follows re-remembers it. */
export function forgetTotpFactors(userId) {
  if (userId) cache.delete(userId);
}

/** Sign-out hook (`clearPerUserDeviceState`) — never allowed to leak to the
 *  next user on a shared browser. */
export function clearTotpFactorsSnapshot() {
  cache.clear();
}
